import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, fmtPrice } from "../lib/api";
import { toast } from "sonner";

export const CartSheet = () => {
  const { items, open, setOpen, remove, setQty, total } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = { items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })), origin_url: window.location.origin };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-[#0B0E14]/40 backdrop-blur-[2px] z-[60]"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cart-sheet"
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#EDEEF1] z-[70] flex flex-col border-l border-[#D4D7DD]"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-[#D4D7DD]">
              <span className="label">Your Bag ({items.length})</span>
              <button data-testid="cart-close" onClick={() => setOpen(false)}><X strokeWidth={1.3} /></button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center gap-2 text-[#8A8F99]">
                  <p className="font-serif-display text-2xl text-[#0B0E14]">Your bag is empty</p>
                  <p className="label">Selected pieces will appear here</p>
                </div>
              ) : (
                items.map((i) => (
                  <div key={i.key} data-testid="cart-item" className="flex gap-4 p-6 border-b border-[#D4D7DD]">
                    <img src={i.image} alt={i.name} className="w-20 h-28 object-cover" />
                    <div className="flex-1 flex flex-col">
                      <div className="flex justify-between">
                        <p className="font-serif-display text-lg leading-tight">{i.name}</p>
                        <button onClick={() => remove(i.key)} data-testid="cart-remove"><X className="w-4 h-4" /></button>
                      </div>
                      <p className="label text-[#8A8F99] mt-1">Size {i.size}</p>
                      <div className="mt-auto flex items-center justify-between">
                        <div className="flex items-center border border-[#0B0E14]">
                          <button className="px-2 py-1" onClick={() => setQty(i.key, i.quantity - 1)}><Minus className="w-3 h-3" /></button>
                          <span className="px-3 text-sm">{i.quantity}</span>
                          <button className="px-2 py-1" onClick={() => setQty(i.key, i.quantity + 1)}><Plus className="w-3 h-3" /></button>
                        </div>
                        <span className="text-sm">{fmtPrice(i.price * i.quantity)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#D4D7DD] p-6 space-y-4">
                <div className="flex justify-between items-baseline">
                  <span className="label">Subtotal</span>
                  <span data-testid="cart-total" className="font-serif-display text-2xl">{fmtPrice(total)}</span>
                </div>
                <p className="label text-[#8A8F99]">Taxes & shipping calculated at checkout</p>
                <button
                  data-testid="checkout-button"
                  disabled={loading}
                  onClick={checkout}
                  className="w-full bg-[#0B0E14] text-[#EDEEF1] h-14 label hover:bg-[#1B2233] transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? "Redirecting…" : "Proceed to Checkout"}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
